import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TeamsService } from '../../../services/teams/teams.service';

export type EmployeeDetailsDialogData = {
  teamId?: string;
  employee: any;
  allowEditing?: boolean;
};

export type EmployeeDetailsDialogResult =
  | { saved: true; employee: any }
  | { saved: false };

type FieldRow = { label: string; value: string };
type OrderedField = { label: string; keys?: string[]; value?: (employee: any) => string | null };

@Component({
  selector: 'app-employee-details-dialog',
  templateUrl: './employee-details-dialog.component.html',
  styleUrls: ['./employee-details-dialog.component.scss'],
})
export class EmployeeDetailsDialogComponent {
  form: FormGroup;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private teamsService: TeamsService,
    private ref: MatDialogRef<EmployeeDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmployeeDetailsDialogData
  ) {
    const employee = this.data?.employee || {};
    this.form = this.fb.group({
      employeeNumber: [this.normalizeTextValue(employee?.employeeNumber || employee?.employeeId || employee?.['Employee Id'])],
      employmentDate: [this.normalizeDateInput(employee?.employmentDate)],
      birthDate: [this.normalizeDateInput(employee?.teamuserMetadata?.birthDate ?? employee?.metadata?.birthDate)],
    });
  }

  close(result?: EmployeeDetailsDialogResult): void {
    this.ref.close(result);
  }

  get allowEditing(): boolean {
    return !!this.data?.allowEditing;
  }

  get title(): string {
    const e = this.data?.employee || {};
    const first = e?.['First Name'] ?? e?.firstName ?? '';
    const last = e?.['Last Name'] ?? e?.lastName ?? '';
    const name = `${first} ${last}`.trim();
    return name ? `Employee ${name}` : 'Employee details';
  }

  get subtitle(): string {
    return this.allowEditing
      ? 'Payroll-synced fields are read-only here. You can update Wallit-managed employee info below.'
      : 'Employee details shown here come from payroll and Wallit records.';
  }

  get readonlyFields(): FieldRow[] {
    const e = this.data?.employee || {};
    const orderedFields: OrderedField[] = [
      { label: 'Primary Email', keys: ['Primary Email', 'PrimaryEmail', 'primaryEmail', 'email', 'userEmail'] },
      { label: 'Employee Status', value: (employee) => this.memberStatus(employee) },
      { label: 'Source Status', keys: ['Source Status', 'SourceStatus', 'sourceStatus'] },
      { label: 'Job Title', keys: ['Job Title', 'JobTitle', 'jobTitle'] },
      { label: 'Department', keys: ['Department', 'department', 'Default Department Full Path', 'dept'] },
      { label: 'Manager', keys: ['Manager', 'manager'] },
      { label: 'Pay Group', keys: ['Pay Group', 'PayGroup', 'payGroup'] },
      { label: 'Division', keys: ['Division', 'division', 'Default Division Full Path'] },
      { label: 'Location', keys: ['Location', 'location', 'WorkLocation', 'Work Location', 'Default Location Full Path'] },
      { label: 'State', keys: ['State', 'state'] },
      { label: 'Worker Type', keys: ['Worker Type', 'WorkerType', 'workerType'] },
      { label: 'Employment Type', keys: ['Employment Type', 'employeeType', 'Employee Type', 'employmentType', 'employment_type'] },
      { label: 'Pay Type', keys: ['Pay Type', 'payType'] },
      { label: 'Team Join Date', value: (employee) => this.formatDate(employee?.memberCreatedAt) },
    ];

    return orderedFields
      .map((field): FieldRow | null => {
        const value = field.value ? field.value(e) : this.resolveFieldValue(e, field.keys || []);
        if (value === null) {
          return null;
        }
        return { label: field.label, value };
      })
      .filter((row): row is FieldRow => !!row);
  }

  get saveDisabled(): boolean {
    return this.isSaving || !this.allowEditing || !this.hasEditableChanges;
  }

  get hasEditableChanges(): boolean {
    const employee = this.data?.employee || {};
    return (
      this.normalizeTextValue(this.form.value.employeeNumber) !==
        this.normalizeTextValue(employee?.employeeNumber || employee?.employeeId || employee?.['Employee Id']) ||
      this.normalizeDateInput(this.form.value.employmentDate) !== this.normalizeDateInput(employee?.employmentDate) ||
      this.normalizeDateInput(this.form.value.birthDate) !==
        this.normalizeDateInput(employee?.teamuserMetadata?.birthDate ?? employee?.metadata?.birthDate)
    );
  }

  async save(): Promise<void> {
    if (!this.allowEditing || this.saveDisabled) {
      return;
    }

    const employee = this.data?.employee || {};
    const teamId = this.data?.teamId || '';
    const memberId = employee?.memberId || employee?.id || '';
    if (!teamId || !memberId) {
      return;
    }

    const employeeNumber = this.normalizeNullableTextValue(this.form.value.employeeNumber);
    const employmentDate = this.normalizeNullableDateValue(this.form.value.employmentDate);
    const birthDate = this.normalizeNullableDateValue(this.form.value.birthDate);
    const originalBirthDate = employee?.teamuserMetadata?.birthDate ?? employee?.metadata?.birthDate;
    const storedBirthDate = this.normalizeDateInput(originalBirthDate);
    const nextMetadata = {
      ...(employee?.teamuserMetadata && typeof employee.teamuserMetadata === 'object'
        ? employee.teamuserMetadata
        : {}),
    };

    delete nextMetadata.birthDate;
    if (this.normalizeDateInput(this.form.value.birthDate) !== storedBirthDate) {
      nextMetadata.birthDate = birthDate;
    }

    // Unchanged valid dates are intentionally omitted so unrelated edits modify neither store;
    // MM-5565 owns backfill. A malformed stored value renders empty and remains unclearable here.
    const echoedMetadata = { ...nextMetadata };
    if (!Object.prototype.hasOwnProperty.call(nextMetadata, 'birthDate') && originalBirthDate !== undefined) {
      echoedMetadata.birthDate = originalBirthDate;
    }

    this.isSaving = true;
    try {
      await this.teamsService.modifyTeamMember(teamId, memberId, {
        employeeNumber,
        employmentDate,
        metadata: nextMetadata,
      });

      this.close({
        saved: true,
        employee: {
          ...employee,
          employeeNumber,
          employmentDate,
          // `metadata` here is the joined users row, which this save does not touch.
          // `...employee` carries it through unchanged; only teamuserMetadata is updated.
          teamuserMetadata: {
            ...(employee?.teamuserMetadata && typeof employee.teamuserMetadata === 'object'
              ? employee.teamuserMetadata
              : {}),
            ...echoedMetadata,
          },
        },
      });
    } finally {
      this.isSaving = false;
    }
  }

  private resolveFieldValue(employee: any, keys: string[]): string | null {
    for (const key of keys) {
      const value = this.valueToString(employee?.[key]);
      if (value !== null) {
        return value;
      }
    }
    return null;
  }

  private memberStatus(employee: any): string | null {
    return this.valueToString(
      employee?.message ||
      employee?.employeeStatus ||
      employee?.EmployeeStatus ||
      employee?.employmentStatus ||
      employee?.EmploymentStatus ||
      employee?.['Employee Status'] ||
      this.getMemberInviteStatus(employee)
    );
  }

  private getMemberInviteStatus(employee: any): string {
    if (this.hasCompletedMemberAccount(employee) || employee?.inviteAcceptedAt) {
      return 'Active';
    }
    if (employee?.inviteSentAt) {
      return 'Pending Sign Up';
    }
    return 'Pending Invitation';
  }

  private hasCompletedMemberAccount(employee: any): boolean {
    return !!employee?.userId
      && !!`${employee?.firstName || employee?.['First Name'] || ''}`.trim()
      && !!`${employee?.lastName || employee?.['Last Name'] || ''}`.trim()
      && this.isTruthyFlag(employee?.userIsActivatedUser ?? employee?.isActivatedUser)
      && this.isTruthyFlag(employee?.userIsCompleted ?? employee?.isCompleted)
      && !this.isTruthyFlag(employee?.userIsPendingInvite ?? employee?.isPendingInvite);
  }

  private isTruthyFlag(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private formatDate(value: any): string | null {
    const normalized = this.normalizeDateInput(value);
    if (!normalized) {
      return null;
    }

    const date = new Date(`${normalized}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return normalized;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  private normalizeTextValue(value: any): string {
    return `${value ?? ''}`.trim();
  }

  private normalizeNullableTextValue(value: any): string | null {
    const normalized = this.normalizeTextValue(value);
    return normalized || null;
  }

  private normalizeDateInput(value: any): string {
    const normalized = `${value ?? ''}`.trim();
    if (!normalized) {
      return '';
    }

    const match = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) {
      return match[1];
    }

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  }

  private normalizeNullableDateValue(value: any): string | null {
    const normalized = this.normalizeDateInput(value);
    return normalized || null;
  }

  private valueToString(value: any): string | null {
    if (value === undefined || value === null) {
      return null;
    }
    if (Array.isArray(value)) {
      const joined = value.map((v) => `${v}`).join(', ').trim();
      return joined ? joined : null;
    }
    if (typeof value === 'object') {
      try {
        const json = JSON.stringify(value);
        return json && json !== '{}' ? json : null;
      } catch {
        return `${value}`;
      }
    }
    const str = `${value}`.trim();
    return str ? str : null;
  }
}
